/**
 * Foundry's JSON predicate grammar (src/module/system/predication.ts), evaluated over a set of
 * roll options. A predicate is a list of statements, all of which must hold.
 */

export type BinaryOperation =
  | { eq: [string, string | number] }
  | { gt: [string, string | number] }
  | { gte: [string, string | number] }
  | { lt: [string, string | number] }
  | { lte: [string, string | number] };

export type Atom = string | BinaryOperation;

export type CompoundStatement =
  | { and: PredicateStatement[] }
  | { or: PredicateStatement[] }
  | { xor: PredicateStatement[] }
  | { nand: PredicateStatement[] }
  | { nor: PredicateStatement[] }
  | { not: PredicateStatement }
  | { if: PredicateStatement; then: PredicateStatement }
  | { iff: PredicateStatement[] };

export type PredicateStatement = Atom | CompoundStatement;
export type RawPredicate = PredicateStatement[];

const BINARY_OPERATORS = new Set(["eq", "gt", "gte", "lt", "lte"]);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isBinaryOp(statement: unknown): statement is BinaryOperation {
  if (!isPlainObject(statement)) return false;
  const entries = Object.entries(statement);
  if (entries.length !== 1) return false;
  const [operator, operands] = entries[0]!;
  return (
    BINARY_OPERATORS.has(operator) &&
    Array.isArray(operands) &&
    operands.length === 2 &&
    typeof operands[0] === "string" &&
    (typeof operands[1] === "string" || typeof operands[1] === "number")
  );
}

function isStatementList(value: unknown): value is PredicateStatement[] {
  return Array.isArray(value) && value.every((s) => isStatement(s));
}

export function isCompound(statement: unknown): statement is CompoundStatement {
  if (!isPlainObject(statement)) return false;
  const keys = Object.keys(statement);
  if (keys.length === 1) {
    const key = keys[0]!;
    const value = statement[key];
    switch (key) {
      case "and":
      case "or":
      case "xor":
      case "nand":
      case "nor":
      case "iff":
        return isStatementList(value);
      case "not":
        return !!value && isStatement(value);
      default:
        return false;
    }
  }
  return keys.length === 2 && "if" in statement && "then" in statement && isStatement(statement.if) && isStatement(statement.then);
}

export function isStatement(statement: unknown): statement is PredicateStatement {
  if (isPlainObject(statement)) return isCompound(statement) || isBinaryOp(statement);
  return typeof statement === "string" && statement.length > 0;
}

/** Is this an array of predication statements? */
export function isValidPredicate(value: unknown): value is RawPredicate {
  return Array.isArray(value) && value.every((s) => isStatement(s));
}

/** Test a predicate against a domain of discourse. A malformed predicate is false. */
const POST_ROLL_ATOM = /^check:(?:roll:)?total(?::[a-z]+)*(?::-?\d+)?$/;

/** Every plain atom in a predicate, `check:total:*` ones (known only once the die is rolled) and the rest apart. */
export function predicateAtoms(predicate: RawPredicate): { postRoll: string[]; other: string[] } {
  const postRoll = new Set<string>();
  const other = new Set<string>();
  const walk = (value: unknown): void => {
    if (typeof value === "string") (POST_ROLL_ATOM.test(value) ? postRoll : other).add(value);
    else if (Array.isArray(value)) value.forEach(walk);
    else if (typeof value === "object" && value !== null) Object.values(value).forEach(walk);
  };
  walk(predicate);
  return { postRoll: [...postRoll], other: [...other] };
}

/**
 * Test a predicate before the die is rolled: `check:total:*` atoms are assumed to hold. Returns
 * whether it passes and, when such atoms exist, that the outcome still depends on the roll.
 */
export function testBeforeRoll(predicate: RawPredicate, options: Set<string> | Iterable<string>): { passes: boolean; postRoll: boolean } {
  const atoms = predicateAtoms(predicate);
  if (atoms.postRoll.length === 0) return { passes: testPredicate(predicate, options), postRoll: false };
  return { passes: testPredicate(predicate, new Set([...options, ...atoms.postRoll])), postRoll: true };
}

/** Test a predicate once the die is rolled, with every atom not about the roll assumed true (they were tested before). */
export function testAfterRoll(predicate: RawPredicate, postRollOptions: Iterable<string>): boolean {
  return testPredicate(predicate, new Set([...postRollOptions, ...predicateAtoms(predicate).other]));
}

export function testPredicate(predicate: RawPredicate, options: Set<string> | Iterable<string>): boolean {
  if (predicate.length === 0) return true;
  if (!isValidPredicate(predicate)) return false;
  const domain = options instanceof Set ? options : new Set(options);
  return predicate.every((s) => isTrue(s, domain));
}

function isTrue(statement: PredicateStatement, domain: Set<string>): boolean {
  if (typeof statement === "string") return domain.has(statement);
  if (isBinaryOp(statement)) return testBinaryOp(statement, domain);
  if (isCompound(statement)) return testCompound(statement, domain);
  return false;
}

function testBinaryOp(statement: BinaryOperation, domain: Set<string>): boolean {
  if ("eq" in statement) {
    const [left, right] = statement.eq;
    return typeof right === "string" ? left === right : domain.has(`${left}:${right}`);
  }
  // Partial statements match numeric suffixes: { gt: ["actor:level", 5] } matches "actor:level:6".
  const [operator, [left, right]] = Object.entries(statement)[0] as [string, [string, string | number]];
  const getValues = (operand: string | number): number[] => {
    const maybeNumber = Number(operand);
    if (!Number.isNaN(maybeNumber)) return [maybeNumber];
    const pattern = new RegExp(String.raw`^${escapeRegExp(String(operand))}:([^:]+)$`);
    const values: number[] = [];
    for (const option of domain) {
      const n = Number(pattern.exec(option)?.[1] || NaN);
      if (!Number.isNaN(n)) values.push(n);
    }
    return values.length > 0 ? values : [NaN];
  };
  const leftValues = getValues(left);
  const rightValues = getValues(right);
  switch (operator) {
    case "gt": return leftValues.some((l) => rightValues.every((r) => l > r));
    case "gte": return leftValues.some((l) => rightValues.every((r) => l >= r));
    case "lt": return leftValues.some((l) => rightValues.every((r) => l < r));
    case "lte": return leftValues.some((l) => rightValues.every((r) => l <= r));
    default: return false;
  }
}

function testCompound(statement: CompoundStatement, domain: Set<string>): boolean {
  if ("and" in statement) return statement.and.every((s) => isTrue(s, domain));
  if ("nand" in statement) return !statement.nand.every((s) => isTrue(s, domain));
  if ("or" in statement) return statement.or.some((s) => isTrue(s, domain));
  if ("xor" in statement) return statement.xor.filter((s) => isTrue(s, domain)).length === 1;
  if ("nor" in statement) return !statement.nor.some((s) => isTrue(s, domain));
  if ("not" in statement) return !isTrue(statement.not, domain);
  if ("if" in statement) return !(isTrue(statement.if, domain) && !isTrue(statement.then, domain));
  if ("iff" in statement) {
    return statement.iff.every((s) => isTrue(s, domain)) || statement.iff.every((s) => !isTrue(s, domain));
  }
  return false;
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
